import { Trans } from "@lingui/react/macro";
import {
  Bookmark,
  CalendarCheck,
  CalendarPlus,
  FileHeart,
  Heart,
  MessageSquare,
  ShieldCheck,
  Sparkles,
  Star,
  TimerOff,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";

export type AuthIntent =
  | "favorite"
  | "save-search"
  | "book"
  | "appointments"
  | "review"
  | "message"
  | "medical-journal"
  | "referral"
  /** Doctor landing on `/requests/:token` — activate the profile and answer. */
  | "booking-request"
  /** Session ended by the 90-day absolute cap — the user was signed in a moment ago. */
  | "session-expired"
  /** Professional session ended by the 2h idle timeout. */
  | "session-idle";

interface IntentCopy {
  icon: LucideIcon;
  title: ReactNode;
  body: ReactNode;
}

export function getAuthIntentCopy(intent: AuthIntent): IntentCopy {
  switch (intent) {
    case "favorite":
      return {
        icon: Heart,
        title: <Trans>Save your favorite doctors and clinics</Trans>,
        body: (
          <Trans>
            One tap to find them again, plus alerts when new slots open up with the providers you
            care about.
          </Trans>
        ),
      };
    case "save-search":
      return {
        icon: Bookmark,
        title: <Trans>Save this search</Trans>,
        body: (
          <Trans>
            We'll let you know when new doctors match — no more starting from scratch every time.
          </Trans>
        ),
      };
    case "book":
      return {
        icon: CalendarPlus,
        title: <Trans>Book in 30 seconds</Trans>,
        body: (
          <Trans>
            SMS reminders, free cancellation up to 24h before, one-tap reschedule. Your slot is held
            while you confirm.
          </Trans>
        ),
      };
    case "appointments":
      return {
        icon: CalendarCheck,
        title: <Trans>All your appointments in one place</Trans>,
        body: (
          <Trans>
            Past and upcoming visits with attached prescriptions, reports, and follow-up notes.
          </Trans>
        ),
      };
    case "review":
      return {
        icon: Star,
        title: <Trans>Leave a verified review</Trans>,
        body: (
          <Trans>
            Reviews on Pontimed only count after a real consultation. Help other patients and earn
            loyalty points.
          </Trans>
        ),
      };
    case "message":
      return {
        icon: MessageSquare,
        title: <Trans>Message your doctor securely</Trans>,
        body: (
          <Trans>
            Encrypted thread with your provider. Whole conversation in one place, never lost.
          </Trans>
        ),
      };
    case "medical-journal":
      return {
        icon: FileHeart,
        title: <Trans>Build your medical journal</Trans>,
        body: (
          <Trans>
            All your tests, prescriptions, and consultations in one journal — share with a new
            doctor in one tap.
          </Trans>
        ),
      };
    case "referral":
      return {
        icon: Wallet,
        title: <Trans>Recommend a doctor, earn a commission</Trans>,
        body: (
          <Trans>
            Share your link with doctors, clinics and businesses. When they subscribe, you earn a
            commission for up to 3 years.
          </Trans>
        ),
      };
    case "booking-request":
      return {
        icon: CalendarCheck,
        title: <Trans>Activate your profile</Trans>,
        body: (
          <Trans>
            Sign in with the email or phone we wrote to, and the request is yours to answer.
          </Trans>
        ),
      };
    // The two below are not upsells: the person WAS signed in and the server
    // ended the session on purpose (session-policy.ts in packages/auth-server).
    // Without a word of explanation the modal looks like a bug.
    case "session-expired":
      return {
        icon: ShieldCheck,
        title: <Trans>Your session has expired</Trans>,
        body: (
          <Trans>
            For your security, every sign-in expires after 90 days. Sign in again to continue where
            you left off.
          </Trans>
        ),
      };
    case "session-idle":
      return {
        icon: TimerOff,
        title: <Trans>Signed out after inactivity</Trans>,
        body: (
          <Trans>
            Professional accounts are signed out after 2 hours without activity, so patient data is
            never left open on a shared screen. Sign in again to continue.
          </Trans>
        ),
      };
  }
}

export function getDefaultAuthIntentCopy(): IntentCopy {
  return {
    icon: Sparkles,
    title: <Trans>One account for everything</Trans>,
    body: (
      <Trans>
        Appointments, messages, and your medical journal — same login on web, app, and clinic.
      </Trans>
    ),
  };
}
